// utils/qapDat.js — builds the QAP DAT (1601-EQ attachment) from the
// GET /api/client/withholding-tax/:clientId/qap/:year/:quarter data.
//
// No format rule lives here: record tags, delimiters, quoting, name fields,
// character cleaning, rate/amount formats and the file name all come from
// utils/qapLayout.js (PROVISIONAL — see that file). This module only decides
// WHETHER a file can be built (blocking checks) and in what order the records
// go. qapDat.test.js enforces that it carries no layout literals.
import * as layout from './qapLayout';
import { splitIndividualName, splitDatLines, downloadDat } from './datCommon';

const isTin9 = (v) => /^\d{9}$/.test(String(v ?? ''));
const isCentavos = (v) => Number.isSafeInteger(v) && v >= 0;

/** Display name of a detail, for messages and the preview. */
export const payeeLabel = (d) => (d.payeeType === 'individual'
  ? [d.lastName, d.firstName, d.middleName].filter(Boolean).join(', ')
  : d.registeredName) || d.displayName || 'payee';

/**
 * Preview rows (seq, payee, TIN, ATC, base, rate, withheld) in file order —
 * shown even while checks fail.
 */
export function qapPreviewRows(data) {
  return (data?.details || []).map((d, index) => ({
    seq: layout.formatSeq(index),
    payee: payeeLabel(d),
    payeeType: d.payeeType,
    tin9: d.tin9,
    branchCode: d.branchCode,
    atcCode: d.atcCode,
    rateBasisPoints: d.rateBasisPoints,
    taxBaseCentavos: d.taxBaseCentavos,
    taxWithheldCentavos: d.taxWithheldCentavos,
  }));
}

function filerNames(header) {
  if (header?.entityType === 'individual') {
    const n = splitIndividualName(header.name);
    return { entityType: 'individual', registeredName: '', lastName: n.last, firstName: n.first, middleName: n.middle };
  }
  return { entityType: 'corporation', registeredName: header?.name || '', lastName: '', firstName: '', middleName: '' };
}

/**
 * @param {object} data  the qap endpoint's `data`
 * @returns {{ok:true, content:string, filename:string} | {ok:false, errors:string[]}}
 */
export function buildQapDat(data) {
  const errors = [];
  if (!data || !data.header) return { ok: false, errors: ['No QAP data loaded.'] };

  // Server checks block the file (they list the rows to fix on screen).
  for (const c of data.checks || []) {
    if (!c.ok) errors.push(c.message || c.label || c.code);
  }

  const period = layout.quarterPeriod(data.year, data.quarter);
  const h = data.header;
  const branch4 = layout.formatQapBranch(h.branchCode);
  if (!isTin9(h.tin9)) errors.push('Client TIN is required (9 digits).');
  if (branch4 === null) errors.push(`Client branch code "${h.branchCode}" cannot be written as 4 digits.`);
  if (h.entityType !== 'individual' && h.entityType !== 'corporation') errors.push('Client type is required.');
  const filer = filerNames(h);
  if (filer.entityType === 'individual'
    ? !layout.cleanName(filer.lastName) || !layout.cleanName(filer.firstName)
    : !layout.cleanName(filer.registeredName)) errors.push('Client name is required.');

  const details = data.details || [];
  if (!details.length) errors.push('There are no payees to export.');

  const rowErrors = [];
  let totalBase = 0;
  let totalWithheld = 0;
  const prepared = details.map((d, index) => {
    const missing = [];
    const dBranch = layout.formatQapBranch(d.branchCode);
    if (!isTin9(d.tin9)) missing.push('TIN');
    if (dBranch === null) missing.push('4-digit branch code');
    if (d.payeeType === 'individual') {
      if (!layout.cleanName(d.lastName) || !layout.cleanName(d.firstName)) missing.push('last / first name');
    } else if (d.payeeType === 'corporation') {
      if (!layout.cleanName(d.registeredName)) missing.push('registered name');
    } else {
      missing.push('individual / non-individual type');
    }
    if (!String(d.atcCode ?? '').trim()) missing.push('ATC');
    if (!Number.isInteger(d.rateBasisPoints) || d.rateBasisPoints < 0) missing.push('tax rate');
    if (!isCentavos(d.taxBaseCentavos)) missing.push('tax base');
    if (!isCentavos(d.taxWithheldCentavos)) missing.push('tax withheld');
    if (missing.length) rowErrors.push(`${payeeLabel(d)}: missing ${missing.join(', ')}`);
    else {
      totalBase += d.taxBaseCentavos;
      totalWithheld += d.taxWithheldCentavos;
    }
    return {
      index,
      tin9: d.tin9,
      branch4: dBranch,
      entityType: d.payeeType,
      registeredName: d.registeredName,
      lastName: d.lastName,
      firstName: d.firstName,
      middleName: d.middleName,
      period,
      atcCode: d.atcCode,
      rateBasisPoints: d.rateBasisPoints,
      taxBaseCentavos: d.taxBaseCentavos,
      taxWithheldCentavos: d.taxWithheldCentavos,
    };
  });
  if (rowErrors.length) {
    const shown = rowErrors.slice(0, 10);
    const rest = rowErrors.length - shown.length;
    errors.push(`Cannot build the QAP file — ${shown.join('; ')}${rest > 0 ? `; and ${rest} more` : ''}.`);
  }

  // Totals must tie to the server's control and to the 1601-EQ.
  if (!rowErrors.length && data.control) {
    if (totalBase !== data.control.totalTaxBaseCentavos || totalWithheld !== data.control.totalTaxWithheldCentavos) {
      errors.push('Detail totals do not match the control totals.');
    }
  }
  const eq = data.return1601eq;
  if (!eq || eq.item19Centavos === null || eq.item19Centavos === undefined) {
    if (!errors.length) errors.push('No saved 1601-EQ for the quarter.');
  } else if (!rowErrors.length && totalWithheld !== eq.item19Centavos) {
    errors.push('Total tax withheld does not equal 1601-EQ item 19.');
  }

  if (errors.length) return { ok: false, errors: [...new Set(errors)] };

  const records = [
    layout.headerRecord({ tin9: h.tin9, branch4, ...filer, rdoCode: h.rdoCode, period }),
    ...prepared.map((d) => layout.detailRecord(d)),
    layout.controlRecord({ tin9: h.tin9, branch4, period, totalTaxBaseCentavos: totalBase, totalTaxWithheldCentavos: totalWithheld }),
  ];
  const content = layout.fileContent(records);

  // Verify rather than trust construction: one record per line, in order.
  if (splitDatLines(content).length !== records.length) {
    return { ok: false, errors: ['A name produced a line break in the file.'] };
  }

  return { ok: true, content, filename: layout.fileName({ tin9: h.tin9, branch4, period }) };
}

export const downloadQapDat = ({ content, filename }) => downloadDat({ content, filename });
